import { FC, createContext, useReducer, useContext, useCallback } from 'react';
import { Dayjs } from 'dayjs';

import { ActionTypes, IReportValuesProps, IState, Dispatch } from './types';
import reducer from './reducers';

const initialState: IState = {
  first_name: '',
  last_name: '',
  date_of_birth: null,
  report_date: null,
  email: '',
  report: '',
  doctor_info: '',
  lang: '',
  icd: []
};

const ReportValuesStateContext = createContext<IState | undefined>(undefined);
const ReportValuesDispatchContext = createContext<Dispatch | undefined>(
  undefined
);

const ReportValuesProvider: FC<IReportValuesProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <ReportValuesStateContext.Provider value={state}>
      <ReportValuesDispatchContext.Provider value={dispatch}>
        {children}
      </ReportValuesDispatchContext.Provider>
    </ReportValuesStateContext.Provider>
  );
};

const useReportValuesStateContext = (): IState => {
  const context = useContext(ReportValuesStateContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useReportValuesStateContext must be used within a ReportValuesProvider'
    );
  }

  return context;
};

const useReportValuesDispatchContext = () => {
  const dispatch = useContext(ReportValuesDispatchContext);

  if (typeof dispatch === 'undefined') {
    throw new Error(
      'useReportValuesDispatchContext must be used within a ReportValuesProvider'
    );
  }

  const setValue = useCallback(
    (key: string, value: string | Dayjs) => {
      dispatch({ type: ActionTypes.SET_VALUE, data: { key, value } });
    },
    [dispatch]
  );

  const setValues = useCallback(
    (data: IState) => {
      dispatch({ type: ActionTypes.SET_VALUES, data });
    },
    [dispatch]
  );

  return { setValue, setValues };
};

export default ReportValuesProvider;
export { useReportValuesDispatchContext, useReportValuesStateContext };
